import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Meta's Embedded Signup (v4) for the Client's WhatsApp number. Messages go through Twilio, so the signup shares the
 * account with Twilio's partner solution.
 */

export interface EmbeddedSignupSettings {
  appId: string;
  configurationId: string;
  graphVersion: string;
  solutionId?: string | null;
}

/** What the backend needs: the account signup made, and the number verified in it (signup does not return it). */
export interface EmbeddedSignupResult {
  wabaId: string;
  phoneNumber: string;
}

// Minimal typing of the Facebook JS SDK globals we touch, rather than a dependency for three calls.
interface FbLoginResponse {
  authResponse?: { code?: string } | null;
}
declare global {
  interface Window {
    FB?: {
      init(params: { appId: string; autoLogAppEvents: boolean; xfbml: boolean; version: string }): void;
      login(callback: (response: FbLoginResponse) => void, options: Record<string, unknown>): void;
    };
    fbAsyncInit?: () => void;
  }
}

// Loads the SDK once per page. The promise is kept so a remount reuses it instead of injecting again.
let sdkPromise: Promise<void> | null = null;
function loadFacebookSdk(signup: EmbeddedSignupSettings): Promise<void> {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<void>((resolve, reject) => {
    window.fbAsyncInit = () => {
      window.FB!.init({ appId: signup.appId, autoLogAppEvents: true, xfbml: true, version: signup.graphVersion });
      resolve();
    };
    const script = document.createElement("script");
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.onerror = () => {
      sdkPromise = null;
      script.remove();
      reject(new Error("Facebook SDK failed to load"));
    };
    document.body.appendChild(script);
  });
  return sdkPromise;
}

function isFacebookOrigin(origin: string) {
  try {
    const host = new URL(origin).hostname;
    return host === "facebook.com" || host.endsWith(".facebook.com");
  } catch {
    return false;
  }
}

/** Whether a typed number looks international (+ and 8-15 digits): signup's number, and what the backend accepts. */
export function isInternationalNumber(value: string) {
  const digits = value.replace(/[\s()-]/g, "");
  return /^\+[1-9]\d{7,14}$/.test(digits);
}

// "PHONE_NUMBER_SETUP" -> "phone number setup"
const humanise = (value: string) => value.replace(/_/g, " ").toLowerCase();

/**
 * Runs Embedded Signup. `connect` posts the result to the backend and resolves to an error message, or null when the
 * number connected. Pass `enabled` false for a user who may not connect, so the SDK is never loaded for them.
 */
export function useEmbeddedSignup(
  signup: EmbeddedSignupSettings | null,
  enabled: boolean,
  connect: (result: EmbeddedSignupResult) => Promise<string | null>,
  onCancel: (message: string) => void,
) {
  const [sdkReady, setSdkReady] = useState(false);
  const [signingUp, setSigningUp] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The number is asked for before the popup opens; the account arrives with the FINISH message.
  const pending = useRef<{ phoneNumber?: string }>({});
  // Set once the popup returns a code, cleared by FINISH: a FINISH that never comes must not leave the button stuck.
  const finishTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearFinishTimeout = () => {
    if (finishTimeout.current) clearTimeout(finishTimeout.current);
    finishTimeout.current = null;
  };
  useEffect(() => clearFinishTimeout, []);

  // Load the SDK up front: FB.login opens a popup, so it must run inside the click, not after an await.
  useEffect(() => {
    if (!signup || !enabled) return;
    let cancelled = false;
    loadFacebookSdk(signup).then(
      () => !cancelled && setSdkReady(true),
      () => !cancelled && setError("Facebook could not be reached. Check your connection or ad blocker and reload the page."),
    );
    return () => {
      cancelled = true;
    };
  }, [signup, enabled]);

  const finish = useCallback(
    async (wabaId: string) => {
      clearFinishTimeout();
      const { phoneNumber } = pending.current;
      if (!phoneNumber) return;
      pending.current = {};
      setSigningUp(false);
      setConnecting(true);
      try {
        setError(await connect({ wabaId, phoneNumber }));
      } catch {
        setError("We couldn't reach Dolfin to connect the number. Check your connection and try again.");
      } finally {
        setConnecting(false);
      }
    },
    [connect],
  );

  useEffect(() => {
    if (!signup) return;
    function onMessage(event: MessageEvent) {
      if (!isFacebookOrigin(event.origin)) return;
      let message: { type?: string; event?: string; data?: Record<string, string | undefined> };
      try {
        message = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return; // the SDK posts other non-JSON messages on the same channel
      }
      if (message?.type !== "WA_EMBEDDED_SIGNUP") return;
      const data = message.data ?? {};
      if (data.error_message) {
        setSigningUp(false);
        setError(data.error_message);
        return;
      }
      if (message.event?.startsWith("FINISH")) {
        if (!data.waba_id) {
          setSigningUp(false);
          setError("The setup finished without a WhatsApp Business account. Run it again.");
          return;
        }
        void finish(data.waba_id);
      } else if (message.event === "CANCEL") {
        clearFinishTimeout();
        setSigningUp(false);
        pending.current = {};
        onCancel(
          data.current_step
            ? `WhatsApp setup cancelled at the ${humanise(data.current_step)} step`
            : "WhatsApp setup cancelled",
        );
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [signup, finish, onCancel]);

  /** Opens the signup for the number the user typed; it must be the one they verify in the popup. */
  function start(phoneNumber: string) {
    if (!signup || !window.FB) return;
    pending.current = { phoneNumber };
    setError(null);
    setSigningUp(true);
    window.FB.login(
      (response) => {
        // Closed or cancelled; the CANCEL message, if any, says where. A finished signup arrives as FINISH.
        if (!response.authResponse?.code) {
          setSigningUp(false);
          return;
        }
        clearFinishTimeout();
        finishTimeout.current = setTimeout(() => {
          if (!pending.current.phoneNumber) return;
          pending.current = {};
          setSigningUp(false);
          setError("Facebook did not confirm which WhatsApp account was set up. Run the setup again.");
        }, 15_000);
      },
      {
        config_id: signup.configurationId,
        response_type: "code",
        override_default_response_type: true,
        // Re-asks rather than failing when the user is already logged in from an earlier attempt.
        auth_type: "rerequest",
        extras: { setup: signup.solutionId ? { solutionID: signup.solutionId } : {} },
      },
    );
  }

  const busy = signingUp || connecting;
  return { sdkReady, signingUp, connecting, busy, canStart: enabled && !!signup && sdkReady && !busy, error, start };
}

// Meta names tiers TIER_250, TIER_1K, TIER_10K, TIER_100K, TIER_UNLIMITED.
export function whatsAppTierLabel(tier: string | null | undefined) {
  if (!tier) return "Not set yet";
  if (tier === "TIER_UNLIMITED") return "Unlimited";
  const match = /^TIER_(\d+)(K?)$/.exec(tier);
  if (!match) return tier;
  const count = Number(match[1]) * (match[2] ? 1000 : 1);
  return `${count.toLocaleString("en-GB")} a day`;
}

export const WHATSAPP_STAGES = [
  { key: "initial_send", label: "Invoice sent" },
  { key: "due_soon", label: "Due soon reminder" },
  { key: "just_overdue", label: "Just overdue reminder" },
  { key: "overdue", label: "Overdue reminder" },
  { key: "final_notice", label: "Final notice" },
] as const;

export const WHATSAPP_TONES = ["friendly", "direct", "formal"] as const;

// The backend's WhatsAppTemplateCatalogue keys: a "Pay now" / "Review bill" button on Dolfin's payment page.
export const BILL_APPROVAL_TEMPLATE_KEY = "dolfin_bill_needs_approval_v2";

export const arTemplateKey = (stage: string, tone: string) => `dolfin_ar_${stage}_${tone}_v3`;

// A Client that brings its own payment link also has one template per stage with the button on that link, in its
// one WhatsApp tone: dolfin_ar_{stage}_{tone}_link_{hash}. The backend keeps only the current link's.
const ownLinkKey = /^dolfin_ar_(.+)_(friendly|direct|formal)_link_[0-9a-f]{8}$/;

export function ownLinkTemplateKey(stage: string, keys: Iterable<string>) {
  for (const key of keys) if (ownLinkKey.exec(key)?.[1] === stage) return key;
  return undefined;
}

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// Keys are dolfin_ar_{stage}_{tone}_v{n}, dolfin_ar_{stage}_{tone}_link_{hash} and the bill approval; anything else
// shows as is.
export function whatsAppTemplateLabel(key: string) {
  if (key === BILL_APPROVAL_TEMPLATE_KEY) return "Bill needs approval";
  const own = ownLinkKey.exec(key);
  const match = own ?? /^dolfin_ar_(.+)_(friendly|direct|formal)_v\d+$/.exec(key);
  const stage = match && WHATSAPP_STAGES.find((s) => s.key === match[1]);
  return stage ? `${stage.label} (${capitalise(match![2]!)}${own ? ", your link" : ""})` : key;
}

export type WhatsAppTemplateStatus = "Approved" | "Pending" | "NotSubmitted" | "Rejected" | "Paused" | "Disabled";

/** How a status reads on screen. Each app maps the kind to its own colours. */
export type WhatsAppStatusKind = "good" | "warn" | "bad" | "muted";

export const WHATSAPP_TEMPLATE_STATUS: Record<WhatsAppTemplateStatus, { label: string; kind: WhatsAppStatusKind }> = {
  Approved: { label: "Approved", kind: "good" },
  Pending: { label: "In review", kind: "warn" },
  NotSubmitted: { label: "Not submitted", kind: "muted" },
  Rejected: { label: "Rejected", kind: "bad" },
  Paused: { label: "Paused", kind: "bad" },
  Disabled: { label: "Disabled", kind: "bad" },
};

/** Meta's quality rating as the pages show it. */
export const WHATSAPP_QUALITY: Record<string, { label: string; kind: WhatsAppStatusKind }> = {
  GREEN: { label: "High", kind: "good" },
  YELLOW: { label: "Medium", kind: "warn" },
  RED: { label: "Low", kind: "bad" },
};

/** Keyed as the API's `events` object; `stage` matches the template keys so each switch can show its review. */
export const WHATSAPP_EVENTS = [
  { key: "invoiceSend", stage: "initial_send", label: "Invoice sent", description: "The invoice itself, with its payment link." },
  { key: "reminderDueSoon", stage: "due_soon", label: "Due soon reminder", description: "Before the due date." },
  { key: "reminderJustOverdue", stage: "just_overdue", label: "Just overdue reminder", description: "On or just after the due date." },
  { key: "reminderOverdue", stage: "overdue", label: "Overdue reminder", description: "Once the invoice is overdue." },
  { key: "reminderFinalNotice", stage: "final_notice", label: "Final notice", description: "The last reminder." },
  { key: "billNeedsApproval", stage: undefined, label: "Bill needs approval", description: "To approvers who turned on WhatsApp notifications." },
] as const;

// Least advanced review among the templates that may send: the own-link one alone, else the tone's; with no tone
// (the number serves organisations in every tone) any of the three.
const reviewOrder: WhatsAppTemplateStatus[] = ["Rejected", "Paused", "Disabled", "NotSubmitted", "Pending", "Approved"];
export function whatsAppEventReview(
  stage: string | undefined,
  statusByKey: Map<string, WhatsAppTemplateStatus | undefined>,
  tone?: string | null,
): WhatsAppTemplateStatus | undefined {
  const tones = tone ? [tone.toLowerCase()] : WHATSAPP_TONES;
  const own = stage && ownLinkTemplateKey(stage, statusByKey.keys());
  const keys = own ? [own] : stage ? tones.map((t) => arTemplateKey(stage, t)) : [BILL_APPROVAL_TEMPLATE_KEY];
  const statuses = keys.map((k) => statusByKey.get(k)).filter((x): x is WhatsAppTemplateStatus => !!x);
  if (statuses.length === 0) return undefined;
  return reviewOrder.find((r) => statuses.includes(r));
}
